import type { AvailableMember } from "@/hooks/useAvailbleMember";
import { TableCell, TableRow } from "@/components/ui/table";
import { cn } from "@/utils/cn";
import dayjs from "dayjs";

interface AvailableMemberRowProps {
  member: AvailableMember;
  index: number;
  isHighlighted?: boolean;
  isSelected?: boolean;
  onToggleSelection?: (nurseId: string) => void;
  onRowClick?: (e: React.MouseEvent) => void;
}

/**
 * 메디통 병원 근무자 전용 Row 컴포넌트
 * No. 사번 이름 직종 성별 나이 경력 입사일 생년월일 연락처 소속만 표시
 */
export function AvailableMemberRow({
  member,
  index,
  isHighlighted = false,
  isSelected = false,
  onToggleSelection,
  onRowClick
}: AvailableMemberRowProps) {
  // 생년월일로 나이 계산
  const calculateAge = (birthDate: string): number | null => {
    if (!birthDate) return null;
    const birth = dayjs(birthDate);
    if (!birth.isValid()) return null;
    return dayjs().diff(birth, "year");
  };

  const age = calculateAge(member.birth_date);
  const formattedBirthDate = member.birth_date
    ? dayjs(member.birth_date).format("YYYY-MM-DD")
    : "-";
  const formattedJoiningDate = member.joining_date
    ? dayjs(member.joining_date).format("YYYY-MM-DD")
    : "-";
  const affiliation = member.small_kind_name || member.mb_part_name || "-";

  const handleClick = (e: React.MouseEvent) => {
    if (onRowClick) {
      onRowClick(e); // Table에서 shift 로직 처리
    } else {
      onToggleSelection?.(member.nurse_id); // fallback
    }
  };

  return (
    <TableRow
      className={cn(
        "bg-white group transition-colors cursor-pointer select-none",
        isHighlighted && "bg-blue-100",
        isSelected && "!bg-[#D4F0FF]"
      )}
      data-nid={member.nurse_id}
      onClick={handleClick}
    // onClick={() => onToggleSelection?.(member.nurse_id)}
    >
      {/* No. */}
      <TableCell className="text-center">{index + 1}</TableCell>
      {/* 사번 */}
      <TableCell className="text-center">{member.emp_num || "-"}</TableCell>
      {/* 이름 */}
      <TableCell className="text-center">{member.name || "-"}</TableCell>
      {/* 직종 */}
      <TableCell className="text-center">{member.duty || "-"}</TableCell>
      {/* 성별 */}
      <TableCell className="text-center">
        {member.gender === "M" || member.gender === "남"
          ? "남"
          : member.gender === "F" || member.gender === "여"
            ? "여"
            : member.gender || "-"}
      </TableCell>
      {/* 나이 */}
      <TableCell className="text-center">
        {age !== null ? `${age}세` : "-"}
      </TableCell>
      {/* 경력 */}
      <TableCell className="text-center">{member.career !== null ? `${member.career}년` : "-"}</TableCell>
      {/* 입사일 */}
      <TableCell className="text-center">{formattedJoiningDate}</TableCell>
      {/* 생년월일 */}
      <TableCell className="text-center">{formattedBirthDate}</TableCell>
      {/* 연락처 */}
      <TableCell className="text-center">
        {member.phone_number || "-"}
      </TableCell>
      {/* 소속 */}
      <TableCell className="text-center">{affiliation}</TableCell>
    </TableRow>
  );
}
